import { Client as ConversationsClient } from "@twilio/conversations";

import { findIndex } from "src/assets/scripts/utilities/helpers";

import { getTwilioToken } from "src/api/chat";

import { debounce } from "quasar";

export function useConversations() {
  const token = ref({
    poshn: null,
    user: null,
  });

  const refreshToken = async (identityType) => {
    unref(token)[identityType] = await getTwilioToken(
      unref(conversationsClient)[identityType].user.identity,
    );

    unref(conversationsClient)[identityType].updateToken(
      unref(token)[identityType],
    );
  };

  const conversationsClient = ref({
    poshn: null,
    user: null,
  });

  const loadingConversations = ref(true);

  const resetLoadingState = debounce(() => {
    loadingConversations.value = false;
  }, 10);

  const conversations = ref({
    poshn: [],
    user: [],
  });

  const getConversationBySid = async (conversationSid) => {
    if (conversationSid) {
      let conversation = unref(conversations).user.find(
        (c) => c.sid === conversationSid,
      );

      if (!conversation) {
        conversation = unref(conversations).poshn.find(
          (c) => c.sid === conversationSid,
        );
      }

      if (conversation) {
        return conversation;
      }

      try {
        conversation =
          await unref(conversationsClient).user.getConversationBySid(
            conversationSid,
          );
      } catch {
        console.error("User conversation not found.");
      }

      if (!conversation) {
        conversation =
          await unref(conversationsClient).poshn.getConversationBySid(
            conversationSid,
          );
      }

      return conversation;
    }

    return null;
  };

  const getConversationUnreadCount = async (conversation) => {
    try {
      const userConversation = unref(conversations).user.find(
        (c) => c.sid === conversation.sid,
      );

      if (!userConversation.lastMessage) {
        return 0;
      }

      let unreadCount = await userConversation.getUnreadMessagesCount();

      if (userConversation.lastReadMessageIndex === null) {
        unreadCount = await userConversation.getMessagesCount();
      }

      return unreadCount;
    } catch {
      return 0;
    }
  };

  const conversationHasUnreadMessages = async (conversation) => {
    const unreadCount = await getConversationUnreadCount(conversation);

    return unreadCount > 0;
  };

  // Unread conversations state
  const unreadConversationsCount = ref(0);
  const cachedUnreadResults = ref(new Map());

  // Calculate unread conversations count and cache results
  const calculateUnreadCount = async () => {
    try {
      const unreadPromises = unref(conversations).user.map(
        async (conversation) => {
          return await conversationHasUnreadMessages(conversation);
        },
      );

      const unreadResults = await Promise.all(unreadPromises);

      const unreadMap = new Map();

      unref(conversations).user.forEach((conversation, index) => {
        unreadMap.set(conversation.sid, unreadResults[index]);
      });

      // Cache the results for reuse in filters
      cachedUnreadResults.value = unreadMap;

      unreadConversationsCount.value = unreadResults.filter(Boolean).length;

      return unreadResults;
    } catch (error) {
      console.error("Error calculating unread count:", error);

      unreadConversationsCount.value = 0;

      cachedUnreadResults.value = new Map();

      return [];
    }
  };

  // Setup polling with stale detection and error recovery
  const {
    startPolling: startCalculateUnreadCountPolling,
    stopPolling: stopCalculateUnreadCountPolling,
    execute: executeCalculateUnreadCount,
  } = usePolling(calculateUnreadCount, {
    label: "conversationsUnreadCount",
    staleTimeout: 60_000,
    pollInterval: 30_000,
  });

  const onUserConversationUpdated = debounce(executeCalculateUnreadCount, 1200);

  onBeforeUnmount(() => {
    unref(conversationsClient).user?.off(
      "conversationUpdated",
      onUserConversationUpdated,
    );

    stopCalculateUnreadCountPolling();
  });

  const getLastMessageDate = (conversation) => {
    // Prefer our custom property (set on messageAdded) over SDK's lastMessage
    if (conversation.lastMessageDateCreated) {
      return new Date(conversation.lastMessageDateCreated);
    }

    if (conversation.lastMessage?.dateCreated) {
      return new Date(conversation.lastMessage.dateCreated);
    }

    return null;
  };

  const sortConversationsByLatestActivity = (identityType) => {
    unref(conversations)[identityType].sort((a, b) => {
      const aLastMessageDate = getLastMessageDate(a);
      const bLastMessageDate = getLastMessageDate(b);

      // Both have last messages - sort by message date (newest first)
      if (aLastMessageDate && bLastMessageDate) {
        return bLastMessageDate - aLastMessageDate;
      }

      // Only a has last message - a should come first
      if (aLastMessageDate && !bLastMessageDate) {
        return -1;
      }

      // Only b has last message - b should come first
      if (!aLastMessageDate && bLastMessageDate) {
        return 1;
      }

      // Neither has last message - sort by conversation creation date (newest first)
      if (a.dateCreated && b.dateCreated) {
        return new Date(b.dateCreated) - new Date(a.dateCreated);
      }

      return 0;
    });

    if (identityType === "user") {
      resetLoadingState();

      executeCalculateUnreadCount();
    }
  };

  const debouncedSortUserConversationsByLatestActivity = debounce(
    () => sortConversationsByLatestActivity("user"),
    100,
  );

  const debouncedSortPoshnConversationsByLatestActivity = debounce(
    () => sortConversationsByLatestActivity("poshn"),
    100,
  );

  const onConversationJoined = async ({ conversation, identityType }) => {
    if (
      unref(conversations)[identityType].some((c) => c.sid === conversation.sid)
    ) {
      return;
    }

    unref(conversations)[identityType].push(conversation);

    if (identityType === "user") {
      debouncedSortUserConversationsByLatestActivity();
    } else {
      debouncedSortPoshnConversationsByLatestActivity();
    }
  };

  const onConversationLeft = ({ conversation, identityType }) => {
    const conversationIndex = findIndex(
      unref(conversations)[identityType],
      conversation,
      "sid",
    );

    if (conversationIndex !== -1) {
      unref(conversations)[identityType].splice(conversationIndex, 1);
    }
  };

  const onMessageAdded = ({ message, identityType }) => {
    const conversation = unref(conversations)[identityType].find(
      (c) => c.sid === message.conversation.sid,
    );

    if (conversation) {
      conversation.lastMessageDateCreated = message.dateCreated;
    }

    if (identityType === "user") {
      debouncedSortUserConversationsByLatestActivity();
    } else {
      debouncedSortPoshnConversationsByLatestActivity();
    }
  };

  const conversationsState = ref({
    poshn: "",
    user: "",
  });

  const onInitialized = ({ identityType }) => {
    unref(conversationsState)[identityType] = "initialized";

    console.info(
      `[Twilio] Client initialized successfully for ${identityType}`,
    );

    if (identityType === "user") {
      unref(conversationsClient).user?.on(
        "conversationUpdated",
        onUserConversationUpdated,
      );

      resetLoadingState();

      startCalculateUnreadCountPolling();
    }
  };

  const onInitFailed = ({ identityType, error }) => {
    unref(conversationsState)[identityType] = "failed";

    console.error(
      `[Twilio] Client initialization failed for ${identityType}`,
      error,
    );

    loadingConversations.value = false;

    // Implement user-facing error handling/retry logic
  };

  const onConnectionError = ({ identityType, data }) => {
    console.error(`[Twilio] Connection error for ${identityType}`, {
      terminal: data.terminal,
      message: data.message,
      httpStatusCode: data.httpStatusCode,
      errorCode: data.errorCode,
    });

    // Implement reconnection logic if not terminal
  };

  const connectionState = ref({
    poshn: "",
    user: "",
  });

  const onConnectionStateChanged = ({ state, identityType }) => {
    unref(connectionState)[identityType] = state;
  };

  const initConversations = async ({ identityType, identity }) => {
    unref(token)[identityType] = await getTwilioToken(identity);

    unref(conversationsClient)[identityType] = new ConversationsClient(
      unref(token)[identityType],
    );

    // Initialization events
    unref(conversationsClient)[identityType].on("initialized", () =>
      onInitialized({ identityType }),
    );

    unref(conversationsClient)[identityType].on("initFailed", (error) =>
      onInitFailed({ identityType, error }),
    );

    // Connection events
    unref(conversationsClient)[identityType].on("connectionError", (data) =>
      onConnectionError({ identityType, data }),
    );

    unref(conversationsClient)[identityType].on(
      "connectionStateChanged",
      (state) => onConnectionStateChanged({ state, identityType }),
    );

    // Token events
    unref(conversationsClient)[identityType].on("tokenAboutToExpire", () =>
      refreshToken(identityType),
    );

    unref(conversationsClient)[identityType].on("tokenExpired", () =>
      refreshToken(identityType),
    );

    // Conversation events
    unref(conversationsClient)[identityType].on(
      "conversationJoined",
      (conversation) => onConversationJoined({ conversation, identityType }),
    );

    unref(conversationsClient)[identityType].on(
      "conversationLeft",
      (conversation) => onConversationLeft({ conversation, identityType }),
    );

    // Message events
    unref(conversationsClient)[identityType].on("messageAdded", (message) =>
      onMessageAdded({ message, identityType }),
    );
  };

  const { user } = storeToRefs(useAuthStore());
  const { normalizePhone } = useChatUtils();

  onBeforeMount(() => {
    initConversations({
      identityType: "poshn",
      identity: import.meta.env.CLIENT_TWILIO_IDENTITY,
    });

    initConversations({
      identityType: "user",
      identity: normalizePhone(unref(user).phone),
    });
  });

  return {
    loadingConversations,

    conversationsClient,
    conversationsState,
    connectionState,

    conversations,

    token,

    unreadConversationsCount,
    cachedUnreadResults,

    getConversationBySid,
    getConversationUnreadCount,
    conversationHasUnreadMessages,
    calculateUnreadCount,
    executeCalculateUnreadCount,
  };
}
